import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiLock, FiX, FiArrowRight, FiShield } from 'react-icons/fi';
import { apiCreateCheckoutSession } from '../../services/api';

export default function PaymentModal({ plan, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleCheckout = async () => {
    setLoading(true);
    setError(null);
    try {
      const { url } = await apiCreateCheckoutSession(plan.id);
      window.location.href = url;
    } catch (err) {
      setError(err.message || 'Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-50 flex items-end bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full bg-white rounded-t-3xl px-6 pb-10 pt-6 shadow-2xl"
      >
        <div className="w-10 h-1 bg-stone-200 rounded-full mx-auto mb-5" />

        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-serif text-2xl text-stone-900">Subscribe to {plan.name}</h3>
            <p className="font-sans text-xs text-stone-400 mt-0.5">${plan.price}/year · Cancel anytime</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center text-stone-500"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>

        {/* Plan summary */}
        <div
          className="rounded-2xl p-4 mb-6"
          style={{ background: `${plan.accent}12`, border: `1px solid ${plan.accent}30` }}
        >
          <div className="flex items-center justify-between">
            <span className="font-sans font-semibold text-stone-800">{plan.name} — Annual</span>
            <span className="font-serif text-xl font-semibold" style={{ color: plan.accent }}>
              ${plan.price}
            </span>
          </div>
          <p className="font-sans text-xs text-stone-500 mt-1">Billed once per year · ~${(plan.price / 12).toFixed(2)}/month</p>
        </div>

        {error && (
          <p className="font-sans text-xs text-red-500 text-center mb-4 bg-red-50 rounded-xl py-2 px-3">{error}</p>
        )}

        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={handleCheckout}
          disabled={loading}
          className="w-full py-4 rounded-full font-sans font-semibold text-white text-base shadow-lg mb-4 flex items-center justify-center gap-2"
          style={{ background: `linear-gradient(135deg, ${plan.accent}, ${plan.id === 'glow' ? '#7ecdc4' : '#a855f7'})` }}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeOpacity="0.3" />
                <path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round" />
              </svg>
              Redirecting to checkout…
            </span>
          ) : (
            <>Continue to Checkout <SafeIcon icon={FiArrowRight} /></>
          )}
        </motion.button>

        <div className="flex items-center justify-center gap-4 text-stone-400">
          <div className="flex items-center gap-1">
            <SafeIcon icon={FiLock} className="text-xs" />
            <span className="font-sans text-xs">Secured by Stripe</span>
          </div>
          <div className="flex items-center gap-1">
            <SafeIcon icon={FiShield} className="text-xs" />
            <span className="font-sans text-xs">Cancel anytime</span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
